import { API_BASE_URL } from '@synergy/config';
import { Button, Rating, Stack, Theme, Tooltip } from '@mui/material';
import { Component, ComponentProps, ReactElement } from 'react';
import { DocumentStatus, DocumentType, ReceiptAlert, UserRole } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloHistory, PaloRecordPager, PaloShortcutBinding } from '@synergy/components-palo';
import { PoolSpinner, WithRouterProps, withRouter } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import {
    customerMediaSelector,
    internalMediaSelector,
    priceMatrixMediaSelector,
    supplierMediaSelector,
} from '@synergy/data-access';
import { hasRole, padZeroLeft } from '@synergy/utils';
import { resetUploads } from '@synergy/data-access';
import { tokens } from '@synergy/theme-palo';
import TimelineIcon from '@mui/icons-material/Timeline';
import urljoin from 'url-join';

import { AppState } from '../../../store/models/app-state';
import { ConvertOutcome } from '../../convert-receipt/models/convert-outcome';
import { DetailStream } from '../../activity-sidebar/details/detail-stream';
import { Inquiry } from '../models/inquiry';
import { InquiryDetailDispatchList } from '../containers/inquiry-detail-dispatch-list';
import { InquiryDetailOffers } from '../containers/inquiry-detail-offers';
import { InquiryDetailPositions } from '../containers/inquiry-detail-positions';
import { InquiryDetailSuppliers } from '../containers/inquiry-detail-suppliers';
import {
    InquiryDispatchSlotProvider,
    InquiryInDispatchSlot,
    InquirySuppliersTabName,
    setInquiryOpenOffers,
} from './inquiry-dispatch-slot';
import { InquirySteps } from '../models/inquiry-steps';
import { Offer } from '../../../common/models/offer';
import { OfferTransition } from '../../convert-receipt/models/offer/offerTransition';
import { PaloCompleteDocumentDialog } from '../../../common/components/palo/palo-complete-document-dialog';
import { PaloDetailHeader } from '../../detail-header/palo-detail-header';
import { PaloDirtyProvider } from '../../../common/components/palo/palo-dirty-context';
import { PaloDocumentChain, PaloDocumentChainTabName } from '../../document-chain/palo-document-chain';
import { PaloDocumentChainCreate, PaloDocumentChainProvider } from '../../document-chain/palo-document-chain-context';
import { PaloDocumentPhase, PaloFlowAction, paloDocumentPhase } from '../../detail-header/palo-main-action';
import { PaloDocumentTitle } from '../../../common/components/palo/palo-document-title';
import { PaloInPositionsSlot, PaloPositionsSlotProvider } from '../../../common/components/palo/palo-positions-slot';
import { PaloInquiryAttachments } from './palo-inquiry-attachments';
import { PaloInquiryDetailOverview } from './palo-inquiry-detail-overview';
import { PaloRecordDock } from '../../../common/components/palo/palo-record-dock';
import { PoolStep } from '../../../common/models/step';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { ReceiptNoteType } from '../../../common/models/enums/receipt-note-type';
import { Settings } from '../../../common/models/settings';
import { SupplierWithContact } from '../models/supplier-with-contact';
import { Transition } from '../../convert-receipt/models/type-transition';
import { User } from '../../../common/models/user';
import { activitiesSelector } from '../../../common/selectors/activity-selectors';
import {
    createOrUpdateInquiry,
    deleteInquiry,
    deleteReceiptAlert,
    fetchInquiry,
    finishInquiry,
    rate,
    resetInquiryState,
    updateReceiptAlert,
} from '../inquiry-actions';
import {
    fetchActivities,
    resetActivities,
    resetDocumentType,
    setDocumentType,
} from '../../../common/actions/activity-actions';
import { fetchReceiptInternalNotes, resetReceiptInternalNotes } from '../../../common/actions/internal-notes-actions';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { receiptInternalNotesSelector } from '../../../common/selectors/internal-notes-selectors';
import { sendInquiryOffers } from '../../../common/actions/offer-actions';
import { transitionInquiryIntoPurchaseOrders } from '../../convert-receipt/transition-actions';
import { withPaloTheme } from '../../../common/components/palo/with-palo-theme';

// Module scope so the wrapped steps keep a stable component identity — a
// wrapper created per render would remount the step subtree every time.
const PaloThemedPositions = withPaloTheme(InquiryDetailPositions);

/**
 * The customers with their positions, drawn into the place the overview keeps
 * for them under its head. They stay a step of their own — state, form and
 * validation unchanged, one save writes both — only where they are drawn moves
 * (#2252, as the order does since #2208).
 */
const PaloSlottedPositions = (props: ComponentProps<typeof PaloThemedPositions>): ReactElement => (
    <PaloInPositionsSlot>
        <PaloThemedPositions {...props} />
    </PaloInPositionsSlot>
);
const PaloThemedSuppliers = withPaloTheme(InquiryDetailSuppliers);
const PaloThemedDispatchList = withPaloTheme(InquiryDetailDispatchList);

/**
 * The dispatch list, drawn at the top of the suppliers tab: who gets the
 * inquiry, to which address, and where each of them stands. It stays a step
 * of its own — state, form and the sending unchanged — only where it is drawn
 * moves (#2258, the way the positions moved in #2252).
 */
const PaloSlottedDispatchList = (props: ComponentProps<typeof PaloThemedDispatchList>): ReactElement => (
    <InquiryInDispatchSlot>
        <PaloThemedDispatchList {...props} />
    </InquiryInDispatchSlot>
);
const PaloThemedOffers = withPaloTheme(InquiryDetailOffers);

const styles = (theme: Theme) =>
    createStyles({
        spinner: {
            flex: 1,
        },
        container: {
            display: 'flex',
            overflow: 'hidden',
            height: '100%',
            flex: 1,
            flexDirection: 'column',
        },
        scrollContent: {
            marginTop: 2,
            flex: 1,
        },
        content: {
            height: '100%',
        },
    });

interface InquiryDetailProps extends WithStyles<typeof styles>, WithRouterProps {
    fetchInquiry: (id: number) => void;
    createOrUpdateInquiry: (inquiry: Partial<Inquiry>, inquiryId?: number) => void;
    finishInquiry: (inquiry: Inquiry) => void;
    resetInquiryState: () => void;
    resetUploads: () => void;
    fetchActivities: (docType: DocumentType, docId: number) => void;
    resetActivities: () => void;
    fetchTransitions: (docType: DocumentType, docId: number) => void;
    resetTransitions: () => void;
    sendInquiryOffers: (inquiryId: number, supplierIds: SupplierWithContact[]) => void;
    deleteInquiry: (id: number) => void;
    transitionInquiryIntoPurchaseOrders: (transitionObject: OfferTransition) => Promise<ConvertOutcome>;
    setDocumentType: (documentType: DocumentType) => void;
    resetDocumentType: () => void;
    updateReceiptAlert: (id: number, alert: ReceiptAlert) => void;
    deleteReceiptAlert: (id: number) => void;
    rate: (id: number, rating: number) => void;
    fetchReceiptInternalNotes: (noteType: ReceiptNoteType, entityId: number) => void;
    resetReceiptInternalNotes: () => void;
    entity?: Inquiry;
    transitionsFromCount: number;
    activityCount: number;
    noteCount: number;
    attachmentCount: number;
    settings: Settings;
    user: User;
}

interface InquiryDetailState {
    index: number;
    nextIndex?: number;
    ctaButtonEnabled: boolean;
    // data can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ctaButtonData: any;
    showPriceComparison: boolean;
    unlocked: boolean;
    showFinishConfirmation: boolean;
    rating: number | null;
    paramsId?: number;
}

class PaloInquiryDetailBase extends Component<InquiryDetailProps, InquiryDetailState> {
    private isNew = true;
    private saveIfActivationWasSuccessful = false;
    private resourceId?: number;
    private resource?: Partial<Inquiry>;
    private stepsDone: number[] = [];
    private isLoading = false;

    public state: InquiryDetailState = {
        index: 0,
        ctaButtonEnabled: false,
        ctaButtonData: undefined,
        showPriceComparison: false,
        unlocked: false,
        showFinishConfirmation: false,
        rating: this.props.entity?.rating || null,
        paramsId: undefined,
    };

    public container: HTMLDivElement | null = null;

    private formSteps: PoolStep[] = [
        {
            name: <FormattedMessage id="app.inquiries.detail.step.overview" />,
            component: PaloInquiryDetailOverview,
            tab: 'document',
        },
        {
            name: <FormattedMessage id="app.inquiries.detail.step.positions" />,
            component: PaloSlottedPositions,
            tab: 'document',
        },
        // Picking the suppliers and sending to them are one page: the two
        // tabs showed the same suppliers twice (#2258).
        {
            name: <InquirySuppliersTabName />,
            component: PaloThemedSuppliers,
            tab: 'suppliers',
        },
        {
            name: <FormattedMessage id="app.inquiries.detail.step.dispatchList" />,
            component: PaloSlottedDispatchList,
            tab: 'suppliers',
        },
        {
            name: <FormattedMessage id="app.inquiries.detail.step.offersAndComparisonList" />,
            component: PaloThemedOffers,
            icon: <TimelineIcon color="primary" />,
        },
    ];

    /**
     * The chain tab sits behind the five steps of the wizard but is not one of
     * them — which is why "continue" still stops at the last form step.
     */
    private getSteps = (): PoolStep[] => [
        ...this.formSteps,
        {
            name: <PaloDocumentChainTabName />,
            component: PaloDocumentChain,
            readOnly: true,
            // A document being created is not part of anything yet.
            isActive: !!this.props.entity?.id,
        },
    ];

    private onButtonClick = (save: boolean) => () => {
        const oldIndex = this.state.index;
        const index = oldIndex + 1;

        if (index <= this.formSteps.length - 1) {
            this.saveIfActivationWasSuccessful = save;
            this.setState({ index, nextIndex: index });
        }
    };

    /** Leads to where the recipients are picked; see renderFlowActions. */
    private goToDispatchList = () => this.setState({ index: InquirySteps.DispatchList });

    /**
     * Saves first, then sends, and stays on the tab to show who has it now.
     *
     * The dispatch list shows a supplier the moment he is ticked in the search
     * below it, before anything is saved — and the server sends only to the
     * suppliers the inquiry holds. Sending first and saving after, as it was
     * done while the list only showed saved suppliers, sent nothing to the new
     * ones (#2258).
     */
    private onSendSuppliers = async () => {
        const items = this.state.ctaButtonData as SupplierWithContact[];
        if (!this.resource || !this.resource.id || !items || 0 === items.length) {
            return;
        }

        await (this.props.createOrUpdateInquiry(this.resource, this.resourceId) as unknown as Promise<void>);
        await (this.props.sendInquiryOffers(this.resource.id, items) as unknown as Promise<void>);
    };

    private onfinishInquiry = () => {
        if (!this.props.entity || !this.props.entity.id) {
            return;
        }

        this.props.finishInquiry(this.props.entity);
        this.closeConfirmDialog();
    };

    private closeConfirmDialog = () => {
        this.setState({ showFinishConfirmation: false });
    };

    /**
     * Same rule as the button on the offers step, without its step: the key
     * works from anywhere in the mask, which is the point of having it. An
     * inquiry knows no cancelling, so there is no counterpart to R here.
     */
    private canFinish = (): boolean =>
        DocumentStatus.Ongoing === this.props.entity?.status || DocumentStatus.Created === this.props.entity?.status;

    private createOrUpdateInquiry() {
        if (this.resource) {
            this.props.createOrUpdateInquiry(this.resource, this.resourceId);
        }
    }

    private onGlobalStateChange = (inquiry: Partial<Inquiry>) => {
        this.resource = inquiry;
    };

    private onSave = () => {
        this.createOrUpdateInquiry();
    };

    private onIndexChange = (index: number) => {
        if (index === this.state.nextIndex && this.saveIfActivationWasSuccessful) {
            // Trigger a save the index was changed by the save and continue button
            this.onSave();
        }

        this.setState({ index, nextIndex: undefined });
    };

    private onDelete = async () => {
        if (this.resourceId) {
            await this.props.deleteInquiry(this.resourceId);
            this.props.navigate(-1);
        }
    };

    private onRate = (rating: number | null) => {
        if (this.props.entity?.id && rating) {
            this.props.rate(this.props.entity.id, rating);
        }
    };

    public handleAlertChange = (alert: ReceiptAlert | null) => {
        if (this.props.entity?.id) {
            if (!alert) {
                this.props.deleteReceiptAlert(this.props.entity.id);
            } else {
                this.props.updateReceiptAlert(this.props.entity.id, alert);
            }
        }
    };

    private setDoneSteps = (entity: Inquiry) => {
        this.stepsDone = [];

        if (!entity.title || !entity.endDate) {
            return;
        }
        this.stepsDone.push(InquirySteps.Overview);

        if (
            !entity.accountPurchases ||
            entity.accountPurchases.length === 0 ||
            entity.accountPurchases.some(
                accountPurchase =>
                    !(
                        accountPurchase.customerAccount &&
                        accountPurchase.contact &&
                        accountPurchase.deliveryAddress &&
                        accountPurchase.deliveryDate
                    )
            )
        ) {
            return;
        }
        this.stepsDone.push(InquirySteps.Positions);

        if (!entity.supplierAccounts || entity.supplierAccounts.length === 0) {
            return;
        }
        this.stepsDone.push(InquirySteps.Suppliers);

        const offers =
            (entity &&
                entity.accountPurchases &&
                entity.accountPurchases.reduce(
                    (result: Offer[], purchase) => result.concat(purchase.offers || []),
                    []
                )) ||
            [];
        if (offers.length === 0 && entity.status !== DocumentStatus.Completed) {
            return;
        }
        this.stepsDone.push(InquirySteps.DispatchList);

        if (entity.status === DocumentStatus.Completed) {
            this.stepsDone.push(InquirySteps.OffersAndComparison);
        }
    };

    public handleUnlockedChange = (unlocked: boolean) => {
        this.setState({ unlocked });
    };

    public componentDidMount() {
        setInquiryOpenOffers(() => this.setState({ index: InquirySteps.OffersAndComparison }));
        // Force another render when the portal ref has been updated
        this.forceUpdate();
    }

    public UNSAFE_componentWillMount() {
        this.props.resetInquiryState();
        this.props.resetUploads();
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetReceiptInternalNotes();
        this.props.setDocumentType(DocumentType.Inquiry);

        if (this.props.params['id'] === 'new') {
            return;
        }
        this.isNew = false;
        this.isLoading = true;
        this.resourceId = +this.props.params['id'];
        if (this.resourceId) {
            this.props.fetchInquiry(this.resourceId);
        }
        if (this.props.entity) {
            this.setDoneSteps(this.props.entity);
        }
    }

    public componentWillUnmount() {
        setInquiryOpenOffers(undefined);
        this.props.resetInquiryState();
        this.props.resetUploads();
        this.props.resetActivities();
        this.props.resetTransitions();
        this.props.resetReceiptInternalNotes();
        this.props.resetDocumentType();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: InquiryDetailProps) {
        const paramsId = +nextProps.params['id'];
        if (paramsId !== +this.props.params['id'] && !this.isNew) {
            const inquiryId = +nextProps.params['id'];
            this.props.fetchInquiry(inquiryId);
            this.props.fetchActivities(DocumentType.Inquiry, inquiryId);
            this.props.fetchTransitions(DocumentType.Inquiry, inquiryId);
            return;
        }

        // Only when the server sent the inquiry anew. Run on every change of
        // props, it fetched the activities again each time (#2256). The files
        // come with the server's copy: a note, an offer or a recommendation
        // build a new inquiry here too, with the files it was loaded with,
        // and put an upload just made out of sight (R-194).
        if (nextProps.entity && nextProps.entity !== this.props.entity) {
            if (this.isNew) {
                nextProps.navigate(
                    nextProps.location.pathname.replace(nextProps.params['id'], nextProps.entity.id.toString()),
                    { replace: true }
                );
                this.resourceId = nextProps.entity.id;
                this.isNew = false;
            }
            this.isLoading = false;
            this.props.fetchActivities(DocumentType.Inquiry, nextProps.entity.id);
            this.props.fetchTransitions(DocumentType.Inquiry, nextProps.entity.id);
            this.props.fetchReceiptInternalNotes(ReceiptNoteType.Inquiries, nextProps.entity.id);
            this.setDoneSteps(nextProps.entity);
            if (!this.state.rating) {
                this.setState({ rating: nextProps.entity.rating ?? null });
            }
        }

        this.setState({ paramsId });
    }

    /**
     * What the inquiry itself offers, beside saving.
     *
     * The buttons that only moved on — "Speichern & Weiter", "Weiter" — are
     * gone with the wizard: the steps are tabs, and saving is the one filled
     * button in the header (#2207). What is left does something: send the
     * inquiry to the chosen suppliers, show the selection, finish the inquiry.
     */
    /**
     * The inquiry's own next step. Which of the two it is still depends on the
     * tab one stands on; what the state of the document decides is only whether
     * the step is the filled action (FRM-18).
     */
    /**
     * The inquiry's own next step, chosen by the state of the document.
     *
     * It used to depend on the tab one stood on: "Senden" only in the dispatch
     * list, "Anfrage beenden" only in the offers, so a saved draft showed no
     * main action at all on its first tab (#2210, R-99). The step now follows
     * the status — send while it is a draft, end it once it is out — and is
     * offered wherever one stands.
     *
     * Sending needs the recipients the dispatch list holds, so from another tab
     * the action leads there instead of sending blind. On the dispatch list
     * itself it sends, and keeps doing so after the inquiry is out, then as a
     * plain action beside the main one.
     */
    private renderFlowActions(readonly: boolean, phase: PaloDocumentPhase) {
        const entity = this.props.entity;
        const open = !!entity && (DocumentStatus.Created === entity.status || DocumentStatus.Ongoing === entity.status);
        const onDispatchList =
            InquirySteps.DispatchList === this.state.index || InquirySteps.Suppliers === this.state.index;
        const recipients = Array.isArray(this.state.ctaButtonData) ? this.state.ctaButtonData.length : 0;
        const sendLabel = onDispatchList ? (
            <FormattedMessage id="app.inquiries.detail.sendCount" values={{ count: recipients }} />
        ) : (
            <FormattedMessage id="app.common.send" />
        );

        if (readonly || !open) {
            return null;
        }

        const send = (
            <PaloFlowAction
                id="inquiry-send-button"
                phase={phase}
                kind="advance"
                onClick={onDispatchList ? this.onSendSuppliers : this.goToDispatchList}
                disabled={onDispatchList && !this.state.ctaButtonEnabled}
                // It saves before it sends, so a supplier just ticked does
                // not have to be saved by hand first (R-180).
                savesFirst={onDispatchList}
                blockedReason={<FormattedMessage id="app.inquiries.detail.sendBlocked" />}
            >
                {sendLabel}
            </PaloFlowAction>
        );

        if (DocumentStatus.Created === entity?.status) {
            return send;
        }

        return (
            <>
                {/* Already out: sending to further suppliers stays possible, but
                    it is no longer the step that moves the inquiry on. */}
                {onDispatchList && (
                    <Button
                        id="inquiry-send-button"
                        variant="outlined"
                        color="inherit"
                        size="small"
                        sx={{ color: tokens.color.ink2 }}
                        onClick={this.onSendSuppliers}
                        disabled={!this.state.ctaButtonEnabled}
                    >
                        {sendLabel}
                    </Button>
                )}
                <PaloFlowAction
                    id="inquiry-start-button"
                    phase={phase}
                    kind="advance"
                    onClick={() => this.setState({ showFinishConfirmation: true })}
                >
                    <FormattedMessage id="app.inquiries.detail.finish" />
                </PaloFlowAction>
            </>
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleCtaButtonStateChange = (ctaButtonEnabled: boolean, ctaButtonData: any) => {
        this.setState({ ctaButtonEnabled, ctaButtonData });
    };

    private handleInquiryTransition = (transitionObject: Transition) =>
        this.props.transitionInquiryIntoPurchaseOrders(transitionObject as OfferTransition);

    public render() {
        const { classes, entity, user, settings } = this.props;
        const { index, unlocked } = this.state;

        const isCompleted =
            entity?.status === DocumentStatus.Completed ||
            entity?.status === DocumentStatus.CompletedWithTransition ||
            entity?.status === DocumentStatus.CompletedWithoutTransition;

        const converterAvailable = entity && isCompleted;

        const pdfUrl = (entity && entity.id && urljoin(API_BASE_URL, `/inquiries/${entity.id}/overview`)) || '';

        const readonly = !this.state.unlocked && entity && isCompleted;
        const phase = paloDocumentPhase(entity?.status, { saved: !!entity?.id, unlocked });

        return (
            <PoolSpinner enabled={this.isLoading} sx={{ display: 'block', height: '100%' }}>
                {/* Lends the "Belege" tab the document a follow-up is made from,
                    and the handler that makes it (#2166). */}
                {this.props.entity && (
                    <PaloDocumentChainCreate source={this.props.entity} onConvert={this.handleInquiryTransition} />
                )}
                <PaloDocumentTitle
                    typeId="app.document.inquiry"
                    number={this.state.paramsId ? padZeroLeft(this.state.paramsId, 5) : undefined}
                />
                <PaloDetailHeader
                    steps={this.getSteps()}
                    stepsDone={this.stepsDone}
                    enforceStepOrder={!entity?.id}
                    stepIndex={index}
                    contentPortal={this.container}
                    viewOffers={index === 3}
                    stepperInitialState={entity}
                    currency={settings.currency}
                    entity={entity}
                    isSuperAdmin={hasRole(UserRole.SuperAdmin, user)}
                    unlocked={unlocked}
                    comparesToServer={true}
                    converterAvailable={converterAvailable}
                    transitionBlockedReason={<FormattedMessage id="app.inquiries.detail.transitionBlocked" />}
                    title={entity?.isAuction ? 'app.inquiries.detail.isAuction.true' : 'app.document.inquiry'}
                    breadcrumbs={[
                        {
                            label: <FormattedMessage id="app.navigation.inquiries" />,
                            onClick: () => this.props.navigate(ROUTES.inquiries),
                        },
                    ]}
                    pager={
                        <PaloRecordPager
                            currentId={entity?.id}
                            onOpen={id => this.props.navigate(`${ROUTES.inquiries}/${id}`)}
                        />
                    }
                    pdfUrl={pdfUrl}
                    onUnlockedChange={this.handleUnlockedChange}
                    onGlobalStateChange={this.onGlobalStateChange}
                    onIndexChange={this.onIndexChange}
                    onManualSave={this.onSave}
                    onDelete={this.onDelete}
                    onCtaButtonChanged={this.handleCtaButtonStateChange}
                    onTransition={this.handleInquiryTransition}
                    onAlertChange={this.handleAlertChange}
                    fallbackUrl={ROUTES.inquiries}
                    flowActions={this.renderFlowActions(!!readonly, phase)}
                    saveBehindFlow={
                        DocumentStatus.Created === entity?.status &&
                        (InquirySteps.DispatchList === index || InquirySteps.Suppliers === index)
                    }
                    dock={
                        // Nothing to hang on a document that does not exist yet.
                        !entity?.id ? undefined : (
                            <PaloRecordDock
                                count={this.props.attachmentCount}
                                // The same condition the mask uses. It read
                                // `!unlocked` alone, so the supplier's files were
                                // locked even on a draft — nobody but a superadmin
                                // could ever put a file there.
                                attachments={
                                    <PaloInquiryAttachments
                                        supplierReadOnly={!!readonly}
                                        customerReadOnly={
                                            !unlocked &&
                                            DocumentStatus.Created !== entity?.status &&
                                            DocumentStatus.Ongoing !== entity?.status
                                        }
                                    />
                                }
                                sections={[
                                    {
                                        // How the inquiry turned out is a property of the
                                        // document, not an action on it. Among the buttons
                                        // it sat where the head carries the next step, and
                                        // beside the bookmark star it could be taken for
                                        // one (#2207).
                                        key: 'rating',
                                        label: <FormattedMessage id="app.common.rating" />,
                                        content: (
                                            <Tooltip
                                                title={
                                                    isCompleted ? (
                                                        ''
                                                    ) : (
                                                        <FormattedMessage id="app.common.ratingTooltip" />
                                                    )
                                                }
                                            >
                                                <Stack
                                                    direction="row"
                                                    sx={{
                                                        alignItems: 'center',
                                                        color: theme =>
                                                            !isCompleted ? theme.palette.grey[500] : undefined,
                                                    }}
                                                >
                                                    <Rating
                                                        size="small"
                                                        value={this.state.rating}
                                                        onChange={(_, newValue) => {
                                                            this.setState({ rating: newValue });
                                                            this.onRate(newValue);
                                                        }}
                                                        disabled={!isCompleted}
                                                    />
                                                </Stack>
                                            </Tooltip>
                                        ),
                                    },
                                ]}
                            />
                        )
                    }
                    history={
                        // Nothing to look back on before the first save.
                        entity?.id ? (
                            <PaloHistory>
                                <DetailStream documentType={DocumentType.Inquiry} documentId={entity.id} />
                            </PaloHistory>
                        ) : undefined
                    }
                >
                    <div className={classes.content} ref={ref => (this.container = ref)} />
                </PaloDetailHeader>

                <PaloShortcutBinding
                    combo="f"
                    group="Beleg"
                    label="Beenden"
                    disabled={!this.canFinish()}
                    handler={() => this.setState({ showFinishConfirmation: true })}
                />
                <PaloCompleteDocumentDialog
                    open={this.state.showFinishConfirmation}
                    onClose={this.closeConfirmDialog}
                    onConfirm={this.onfinishInquiry}
                    resourceNumber={this.resource?.number}
                    titleId="app.inquiries.detail.finish"
                    contentMessageId="app.inquiries.detail.finishWarning"
                    // The dialog is headed "Anfrage beenden"; its button said
                    // "Abschließen" — the same step under two words (#2214,
                    // R-52). The demand's dialog says "Beenden" as well.
                    confirmId="app.common.finish"
                    documentType={DocumentType.Inquiry}
                    documentId={entity?.id}
                />
            </PoolSpinner>
        );
    }
}

const InquiryDetailWithRouter = withRouter(PaloInquiryDetailBase);

const mapStateToProps = (state: AppState) => ({
    entity: state.inquiries.entity,
    settings: state.settings,
    user: state.user,
    transitionsFromCount: state.transitionActivities.transitionsFromCount,
    activityCount: activitiesSelector(state).length,
    noteCount: receiptInternalNotesSelector(state).length,
    // Every folder of the rail, the member's and the sent price infos too (#2256).
    attachmentCount:
        internalMediaSelector(state).length +
        supplierMediaSelector(state).length +
        customerMediaSelector(state).length +
        priceMatrixMediaSelector(state).length,
});

const ConnectedInquiryDetail = connect(mapStateToProps, {
    fetchInquiry,
    createOrUpdateInquiry,
    resetInquiryState,
    resetUploads,
    fetchActivities,
    resetActivities,
    sendInquiryOffers,
    deleteInquiry,
    transitionInquiryIntoPurchaseOrders,
    fetchTransitions,
    resetTransitions,
    finishInquiry,
    updateReceiptAlert,
    deleteReceiptAlert,
    setDocumentType,
    resetDocumentType,
    rate,
    fetchReceiptInternalNotes,
    resetReceiptInternalNotes,
})(InquiryDetailWithRouter);

const StyledInquiryDetail = withStyles(styles)(ConnectedInquiryDetail);

/** Each step reports unsaved input under its own key, so the two steps of the document tab mark themselves apart. */
export const PaloInquiryDetail = (props: Record<string, unknown>): ReactElement => (
    <PaloDirtyProvider>
        <PaloDocumentChainProvider documentType={DocumentType.Inquiry}>
            <PaloPositionsSlotProvider>
                <InquiryDispatchSlotProvider>
                    <StyledInquiryDetail {...props} />
                </InquiryDispatchSlotProvider>
            </PaloPositionsSlotProvider>
        </PaloDocumentChainProvider>
    </PaloDirtyProvider>
);
